import React from 'react';
import { FaCompass, FaQuestionCircle, FaBook, FaDollarSign, FaEnvelope, FaInfoCircle, FaFileContract, FaShieldAlt, FaNewspaper, FaUserPlus, FaSignInAlt, FaHome } from 'react-icons/fa';

/**
 * SEO-friendly internal links component that adds crawlable links to pages.
 * Uses proper <a> tags with href attributes for search engine crawlability.
 *
 * @param {string} currentPage - The current page identifier to exclude from links
 * @param {function} onNavigate - Optional navigation handler for SPA navigation
 * @param {string} variant - Display variant: 'compact', 'full', or 'minimal'
 */
const InternalLinks = ({ currentPage, onNavigate, variant = 'full' }) => {
  const allLinks = [
    { id: 'home', label: 'Home', href: '/', icon: FaHome, description: 'Return to homepage' },
    { id: 'about', label: 'About Us', href: '/about', icon: FaInfoCircle, description: 'Learn about ItinerarySeek' },
    { id: 'pricing', label: 'Pricing', href: '/pricing', icon: FaDollarSign, description: 'View our plans' },
    { id: 'blog', label: 'Blog', href: '/blog', icon: FaNewspaper, description: 'Travel tips & guides' },
    { id: 'faq', label: 'FAQ', href: '/faq', icon: FaQuestionCircle, description: 'Frequently asked questions' },
    { id: 'howto', label: 'How To', href: '/howto', icon: FaBook, description: 'User guides' },
    { id: 'contact', label: 'Contact', href: '/contact', icon: FaEnvelope, description: 'Get in touch' },
    { id: 'terms', label: 'Terms of Service', href: '/terms', icon: FaFileContract, description: 'Legal terms' },
    { id: 'privacy', label: 'Privacy Policy', href: '/privacy', icon: FaShieldAlt, description: 'How we protect your data' },
    { id: 'login', label: 'Login', href: '/login', icon: FaSignInAlt, description: 'Sign in to your account' },
    { id: 'register', label: 'Sign Up', href: '/register', icon: FaUserPlus, description: 'Create an account' },
  ];

  // Filter out the current page
  const links = allLinks.filter(link => link.id !== currentPage);

  const handleClick = (e, link) => {
    if (onNavigate) {
      e.preventDefault();
      if (link.id === 'home') {
        onNavigate('landing');
      } else if (link.id === 'login' || link.id === 'register') {
        onNavigate('showAuth', link.id === 'register');
      } else {
        onNavigate(link.id);
      }
    }
  };

  if (variant === 'minimal') {
    // Minimal inline links for text content
    return (
      <div className="mt-6 pt-4 border-t border-neutral-200">
        <p className="text-sm text-neutral-500">
          Explore more:{' '}
          {links.slice(0, 5).map((link, index) => (
            <span key={link.id}>
              <a
                href={link.href}
                onClick={(e) => handleClick(e, link)}
                className="text-primary-500 hover:text-primary-600 hover:underline"
              >
                {link.label}
              </a>
              {index < 4 && ' · '}
            </span>
          ))}
        </p>
      </div>
    );
  }

  if (variant === 'compact') {
    // Compact grid of links
    return (
      <div className="bg-neutral-50 rounded-xl p-6 mt-8">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaCompass className="text-primary-500" />
          Explore More
        </h3>
        <div className="flex flex-wrap gap-3">
          {links.slice(0, 8).map((link) => (
            <a
              key={link.id}
              href={link.href}
              onClick={(e) => handleClick(e, link)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-neutral-200 text-neutral-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 text-sm font-medium"
            >
              <link.icon className="text-primary-400" />
              {link.label}
            </a>
          ))}
        </div>
      </div>
    );
  }

  // Full variant - detailed cards
  return (
    <div className="bg-gradient-to-br from-neutral-50 to-primary-50/30 rounded-2xl p-8 mt-10">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-neutral-800 mb-2 flex items-center justify-center gap-2">
          <FaCompass className="text-primary-500" />
          Explore ItinerarySeek
        </h3>
        <p className="text-neutral-600 text-sm">Discover more features and resources</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {links.slice(0, 8).map((link) => {
          const Icon = link.icon;
          return (
            <a
              key={link.id}
              href={link.href}
              onClick={(e) => handleClick(e, link)}
              className="group flex flex-col items-center p-4 bg-white rounded-xl border border-neutral-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-primary-100 group-hover:bg-primary-500 flex items-center justify-center mb-3 transition-colors duration-300">
                <Icon className="text-xl text-primary-500 group-hover:text-white transition-colors duration-300" />
              </div>
              <span className="font-medium text-neutral-800 group-hover:text-primary-600 text-sm">{link.label}</span>
              <span className="text-xs text-neutral-500 mt-1">{link.description}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default InternalLinks;
